import { cn } from "../../lib/utils.js";
import type { StepItem } from "./Stepper.js";
import type { StepStatus } from "./StepperMarker.js";

/* ---------------- StepperLabel (Stepper's step text) ----------------
   Internal to Stepper (not in the barrel). The sr-only status word carries
   the step's state, so the order and progress are not colour alone (§9). A
   `static` step tracks no progress, so it says nothing. */

const STATUS_LABEL: Record<StepStatus, string | null> = {
  complete: "completed",
  current: "current step",
  upcoming: "upcoming",
  error: "error",
  static: null,
};

export interface StepperLabelProps {
  step: Pick<StepItem, "label" | "description" | "optional">;
  status: StepStatus;
  /** Visually hidden; the name and status word stay in the accessibility
   *  tree (Stepper `labelHidden`). */
  srOnly: boolean;
  vertical: boolean;
}

export function StepperLabel({
  step: { label, description, optional },
  status,
  srOnly,
  vertical,
}: StepperLabelProps) {
  const word = STATUS_LABEL[status];
  return (
    <span
      className={
        srOnly ? "sr-only" : cn("flex flex-col", vertical ? "pb-4" : "")
      }
    >
      <span
        className={cn(
          "text-sm font-medium leading-tight",
          status === "upcoming" ? "text-pb-text-muted" : "text-pb-text",
          status === "error" && "text-pb-red-text",
        )}
      >
        {label}
        {word && <span className="sr-only"> – {word}</span>}
      </span>
      {optional && (
        <span className="text-dense-1 text-pb-text-muted">Optional</span>
      )}
      {description != null && (
        <span className="text-dense-1 text-pb-text-muted">{description}</span>
      )}
    </span>
  );
}
